import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Section, SectionHeader, RowGroup, ToggleRow, RadioRow } from "./_helpers";
import { Eye, EyeOff, MapPin, Activity, BookOpen } from "lucide-react";

function useDebouncedSave(loader, key) {
  const [data, setData] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  useEffect(() => { loader().then(setData).catch(()=>{}); }, []);
  const update = async (patch) => {
    const next = { ...data, ...patch };
    setData(next);
    try {
      await api.patch(`/users/me/${key}`, patch);
      setSavedAt(new Date());
    } catch (e) {
      toast.error(formatApiError(e));
      loader().then(setData).catch(()=>{});
    }
  };
  return { data, update, savedAt };
}

export default function ProfilPage() {
  const { t } = useTranslation();
  const { data, update, savedAt } = useDebouncedSave(
    () => api.get("/users/me/privacy-prefs").then(r => r.data),
    "privacy-prefs"
  );

  const VIS_OPTS = [
    { v: "everyone_logged_in", label: t("settings.common.everyone_logged_in"), desc: t("settings.common.everyone_logged_in_desc") },
    { v: "followers", label: t("settings.common.only_followers"), desc: t("settings.common.only_followers_desc") },
    { v: "friends", label: t("settings.common.only_friends"), desc: t("settings.common.only_friends_desc") },
  ];
  const GUESTBOOK_OPTS = [
    { v: "everyone_logged_in", label: t("settings.common.everyone_logged_in") },
    { v: "followers", label: t("settings.common.only_followers") },
    { v: "friends", label: t("settings.common.only_friends") },
    { v: "vip_only", label: t("settings.common.only_vip") },
    { v: "nobody", label: t("settings.common.nobody_closed") },
  ];
  const LOCATION_OPTS = [
    { v: "city", label: t("settings.profil.loc_city"), desc: t("settings.profil.loc_city_desc") },
    { v: "region", label: t("settings.profil.loc_region"), desc: t("settings.profil.loc_region_desc") },
    { v: "hidden", label: t("settings.profil.loc_hidden"), desc: t("settings.profil.loc_hidden_desc") },
  ];
  const RECORD_VISITS_OPTS = [
    { v: "on", label: t("settings.profil.record_on") },
    { v: "off", label: t("settings.profil.record_off") },
    { v: "invisible_vip", label: t("settings.profil.invisible_mode"), vip: true, desc: t("settings.profil.invisible_mode_desc") },
  ];

  if (!data) return <div className="font-meta text-[var(--cream-soft)]">{t("common.loading")}</div>;

  const p = (k) => t(`settings.profil.${k}`);

  return (
    <>
      <Section testId="visibility-basics-section">
        <SectionHeader icon={Eye} title={p("visibility")} desc={p("visibility_desc")} />
        <RowGroup>
          <ToggleRow testId="show-last-seen" label={p("show_last_seen")} sub={p("show_last_seen_sub")}
            checked={data.show_last_seen} onChange={(v)=>update({show_last_seen:v})} />
          <ToggleRow testId="show-online-dot" label={p("show_online_dot")} sub={p("show_online_dot_sub")}
            checked={data.show_online_dot} onChange={(v)=>update({show_online_dot:v})} />
          <ToggleRow testId="show-my-visitors" label={p("show_my_visitors")} sub={p("show_my_visitors_sub")}
            checked={data.show_my_visitors_to_me} onChange={(v)=>update({show_my_visitors_to_me:v})} />
        </RowGroup>
      </Section>

      <Section testId="record-visits-section">
        <SectionHeader icon={Activity} title={p("when_visiting")} />
        <RowGroup>
          {RECORD_VISITS_OPTS.map((o) => (
            <RadioRow key={o.v} name="record_my_visits" value={o.v} current={data.record_my_visits}
              label={o.label} sub={o.desc} vip={o.vip} testId={`record-visits-${o.v}`}
              onChange={(v) => update({record_my_visits: v})} />
          ))}
        </RowGroup>
      </Section>

      <Section testId="hide-from-lists-section">
        <SectionHeader icon={EyeOff} title={p("hide_from_lists")} />
        <RowGroup>
          <ToggleRow testId="hide-from-search" label={p("hide_from_search")} sub={p("hide_from_search_sub")}
            checked={data.hide_from_search} onChange={(v)=>update({hide_from_search:v})} />
          <ToggleRow testId="hide-from-online" label={p("hide_from_online_list")}
            checked={data.hide_from_online_list} onChange={(v)=>update({hide_from_online_list:v})} />
          <ToggleRow testId="hide-from-creators" label={p("hide_from_creators")} sub={p("hide_from_creators_sub")}
            checked={data.hide_from_creators_catalog} onChange={(v)=>update({hide_from_creators_catalog:v})} />
        </RowGroup>
      </Section>

      <Section testId="profile-visibility-section">
        <SectionHeader icon={Eye} title={p("who_can_see_profile")} />
        <RowGroup>
          {VIS_OPTS.map((o) => (
            <RadioRow key={o.v} name="profile_visibility" value={o.v} current={data.profile_visibility}
              label={o.label} sub={o.desc} testId={`pv-${o.v}`}
              onChange={(v)=>update({profile_visibility:v})} />
          ))}
        </RowGroup>
      </Section>

      <Section testId="gallery-visibility-section">
        <SectionHeader title={p("who_can_see_gallery")} desc={p("who_can_see_gallery_desc")} />
        <RowGroup>
          {VIS_OPTS.map((o) => (
            <RadioRow key={o.v} name="gallery_default_visibility" value={o.v} current={data.gallery_default_visibility}
              label={o.label} testId={`gv-${o.v}`}
              onChange={(v)=>update({gallery_default_visibility:v})} />
          ))}
        </RowGroup>
      </Section>

      <Section testId="stories-visibility-section">
        <SectionHeader title={p("who_can_see_stories")} />
        <RowGroup>
          {VIS_OPTS.map((o) => (
            <RadioRow key={o.v} name="stories_visibility" value={o.v} current={data.stories_visibility}
              label={o.label} testId={`sv-${o.v}`}
              onChange={(v)=>update({stories_visibility:v})} />
          ))}
        </RowGroup>
      </Section>

      <Section testId="guestbook-section">
        <SectionHeader icon={BookOpen} title={p("guestbook")} />
        <p className="font-manrope text-sm text-[var(--cream-soft)] mb-4">{p("guestbook_who_can_write")}</p>
        <RowGroup>
          {GUESTBOOK_OPTS.map((o) => (
            <RadioRow key={o.v} name="guestbook_who_can_write" value={o.v} current={data.guestbook_who_can_write}
              label={o.label} testId={`gb-who-${o.v}`}
              onChange={(v)=>update({guestbook_who_can_write:v})} />
          ))}
        </RowGroup>
        <div className="mt-4">
          <RowGroup>
            <ToggleRow testId="gb-require-approval" label={p("guestbook_require_approval")} sub={p("guestbook_require_approval_sub")}
              checked={data.guestbook_require_approval} onChange={(v)=>update({guestbook_require_approval:v})} />
          </RowGroup>
        </div>
      </Section>

      <Section testId="identity-hide-section">
        <SectionHeader icon={MapPin} title={p("hide_profile_details")} />
        <RowGroup>
          <ToggleRow testId="hide-age" label={p("hide_age")} sub={p("hide_age_sub")}
            checked={data.hide_age} onChange={(v)=>update({hide_age:v})} />
          <ToggleRow testId="hide-activity-feed" label={p("hide_activity_feed")} sub={p("hide_activity_feed_sub")}
            checked={data.hide_activity_feed} onChange={(v)=>update({hide_activity_feed:v})} />
          <ToggleRow testId="hide-follow-counts" label={p("hide_follow_counts")}
            checked={data.hide_follow_counts} onChange={(v)=>update({hide_follow_counts:v})} />
          <ToggleRow testId="allow-friend-requests" label={p("allow_friend_requests")} sub={p("allow_friend_requests_sub")}
            checked={data.allow_friend_requests !== false}
            onChange={(v)=>update({allow_friend_requests:v})} />
          {data.is_creator && (
            <ToggleRow testId="show-creator-link" label={p("show_creator_link")} sub={p("show_creator_link_sub")}
              checked={data.show_creator_link_on_profile === true}
              onChange={(v)=>update({show_creator_link_on_profile:v})} />
          )}
        </RowGroup>
        <div className="mt-6">
          <h3 className="font-manrope text-base text-[var(--cream)] mb-3">{p("location_precision")}</h3>
          <RowGroup>
            {LOCATION_OPTS.map((o) => (
              <RadioRow key={o.v} name="location_precision" value={o.v} current={data.location_precision}
                label={o.label} sub={o.desc} testId={`loc-${o.v}`}
                onChange={(v)=>update({location_precision:v})} />
            ))}
          </RowGroup>
        </div>
      </Section>

      {savedAt && (
        <div className="fixed bottom-6 right-6 bg-[var(--noir-elev)] border border-emerald-500/30 text-emerald-300 px-4 py-2 font-meta text-xs">
          ✓ {t("settings.common.saved")}
        </div>
      )}
    </>
  );
}
